
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { match_redirect_token, get_user } from "@/store/apis/auth";
import {
  SET_USER,
  SET_COMPANY,
  USER_STATE
} from "@/store/modules/auth/constants";
import {
  INIT_COMMON_DATA,
  GET_ROLE_BY_NAME
} from "@/store/modules/common/constants";
import { INIT_RECRUITER } from "@/store/modules/recruiter/constants";
import { ROOT_ERROR } from "@/store/modules/root/constants";
import { UserState } from "@/store/modules/auth/interfaces";
import router from "@/router";

export default Vue.extend({
  name: "RedirectRoute",
  data() {
    return {
      loading: true,
      error: null as string | null,
      success: false
    };
  },
  computed: {
    ...mapGetters("common", {
      get_role_by_name: GET_ROLE_BY_NAME
    })
  },
  async mounted() {
    // Support both 'token' and 'redirect_token' query parameters
    const redirect_token =
      (this.$route.query.redirect_token as string) ||
      (this.$route.query.token as string);

    if (!redirect_token) {
      this.loading = false;
      this.error = "Token parameter is missing from URL";
      return;
    }

    try {
      this.loading = true;

      const ssoResponse = await match_redirect_token(redirect_token);

      if (!ssoResponse || !ssoResponse.data) {
        throw new Error("Invalid response from token validation");
      }

      if (ssoResponse && ssoResponse.data) {
        const user = ssoResponse.data.data.user;
        const token = ssoResponse.data.data.token;
        const company = ssoResponse.data.data.user.companyDetail;
        this.$store.commit("auth/SET_USER", user);
        this.$store.commit("auth/SET_COMPANY", company);

        localStorage.setItem("token", token);
        if (user.refresh_token) {
          localStorage.setItem("refresh_token", token);
        }
        const init_common = await this.init_common_data();
        if (!init_common) {
          this.root_error(this.$t("errors.internal").toString());
          return;
        }

        const recruiter_admin = this.get_role_by_name("recruiter admin");
        if (recruiter_admin.id === user.role_id) {
          await this.fetch_init_recruiter();
          this.set_user_state(UserState.RECRUITER_ADMIN);
        } else {
          // If user is not recruiter admin, show error
          this.root_error(
            "SSO ticket is only valid for recruiter admin users."
          );
        }
      }

      const userResponse = await get_user();
      if (!userResponse || !userResponse.data || !userResponse.data.data) {
        throw new Error("Failed to fetch user data");
      }

      const userData = userResponse.data.data;
      const company = userData.companyDetail;
      delete userData.companyDetail;
      const user = userData;

      this.$store.commit(`auth/${SET_USER}`, user);
      this.$store.commit(`auth/${SET_COMPANY}`, company);

      if (user.refresh_token) {
        localStorage.setItem("refresh_token", user.refresh_token);
      }

      const init_common = await this.init_common_data();
      if (!init_common) {
        this.root_error("Internal Error. Please try again.");
        this.loading = false;
        return;
      }

      this.success = true;
      await router.push("/recruiter/dashboard");
    } catch (err: any) {
      this.root_error(
        err?.response?.data?.message ||
          err?.message ||
          "Invalid or expired token. Please try again."
      );
      this.loading = false;
      this.error =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred while processing the token";
    }
  },
  methods: {
    ...mapMutations({
      root_error: ROOT_ERROR
    }),
    ...mapMutations("auth", {
      set_user_state: USER_STATE
    }),
    ...mapActions("common", {
      init_common_data: INIT_COMMON_DATA
    }),
    ...mapActions("recruiter", {
      init_recruiter: INIT_RECRUITER
    }),
    /**
     * Function to initialize recruiter data
     */
    async fetch_init_recruiter() {
      const recruiter_initialize = await this.init_recruiter();
      // If recruiter not initialized successfully => navigate to error page
      if (!recruiter_initialize) {
        this.set_user_state(UserState.ROOT_ERROR);
        await router.push("/error");
      } else {
        this.set_user_state(UserState.RECRUITER);
        // If redirect URL exist in query => navigate to redirected url
        if (this.$route.query.redirect) {
          await router.push(this.$route.query.redirect as string);
        }
        // If route instead of recruiter route
        // Navigate to recruiter dashboard
        else if (!this.$route.meta?.user_state?.includes(UserState.RECRUITER)) {
          await router.push("/recruiter/dashboard");
        }
      }
    }
  }
});
